import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import "./LoginPage.css";

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const brandText = "grada.live";

  // modes: signin | signup | verify | forgot | reset
  const [mode, setMode] = useState("signin");

  const [csrfToken, setCsrfToken] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // signup fields
  const [fullName, setFullName] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // red error + green notice
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // verify
  const [verifyCode, setVerifyCode] = useState("");

  // reset
  const [resetToken, setResetToken] = useState("");
  const [resetPass, setResetPass] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetChecked, setResetChecked] = useState(false);

  // Detect reset-password token from hash route: "#/reset-password?token=ABC"
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.startsWith("#/reset-password")) return;

    const qIndex = hash.indexOf("?");
    const qs = qIndex >= 0 ? hash.slice(qIndex + 1) : "";
    const token = new URLSearchParams(qs).get("token");

    if (token) {
      setOpen(true);
      setMode("reset");
      setResetToken(token);
    }
  }, []);

  // typewriter on open
  useEffect(() => {
    if (!open) return;
    let i = 0;
    setTyped("");
    const t = setInterval(() => {
      i += 1;
      setTyped(brandText.slice(0, i));
      if (i >= brandText.length) clearInterval(t);
    }, 95);
    return () => clearInterval(t);
  }, [open]);

  // fetch csrf when card opens (needed for /api/register)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch("/api/csrf", { credentials: "include" });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.ok && typeof data.csrfToken === "string") {
          setCsrfToken(data.csrfToken);
        }
      } catch {
        // ignore; register will fail with invalid csrf, user will see error
      }
    })();
  }, [open]);

  // reset token pre-check for better UX
  useEffect(() => {
    if (mode !== "reset" || !resetToken) return;

    setResetChecked(false);
    // don't nuke user's existing notice unless we need to
    setError(null);

    (async () => {
      try {
        const res = await fetch(`/api/reset-password?token=${encodeURIComponent(resetToken)}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => null);

        if (!res.ok || !data?.ok) {
          setError((data && data.error) || "Invalid or expired link.");
        }
      } catch {
        setError("Could not reach the server.");
      } finally {
        setResetChecked(true);
      }
    })();
  }, [mode, resetToken]);

  const safeErrorMessages = new Set([
    "Please fill in all fields.",
    "Name must be between 2 and 40 characters.",
    "Password must be at least 6 characters.",
    "Your university is not supported yet. We're opening new campuses soon.",
    "Too many attempts. Please try again later.",
    "Too many registration attempts. Please try again later.",
    "Invalid or expired verification code.",
    "Invalid or expired link.",
    "Passwords do not match.",
    "Could not reset your password. Please try again.",
    "Please enter your e-mail address.",
    "If this e-mail exists in our system, we sent a reset link.",
  ]);

  function getSafeError(data, fallback) {
    const msg = data && data.error;
    if (msg && safeErrorMessages.has(msg)) return msg;
    return fallback;
  }

  async function handleSignin(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!email || !password) {
      setError("Incorrect email or password.");
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email, password, csrfToken: csrfToken || "" }).toString();

      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => null);

      if (!data?.ok) {
        switch (data?.error) {
          case "invalid_credentials":
          case "NO_SUCH_USER":
            setError("Incorrect email or password.");
            break;
          case "not_verified":
            setError("Verify your email first.");
            setMode("verify");
            break;
          case "too_many_attempts":
            setError("Too many attempts. Try again later.");
            break;
          case "university_not_allowed":
            setError("Your university is not available right now.");
            break;
          case "university_not_configured":
            setError("Account error. Please contact support.");
            break;
          default:
            setError("Login failed. Please try again.");
        }
        setSubmitting(false);
        return;
      }

      // Fetch user data and update auth context
      try {
        const meRes = await fetch("/api/me", { credentials: "include" });
        const meData = await meRes.json().catch(() => null);
        if (meData?.ok && meData.user) {
          const u = meData.user;
          setUser({
            fullName: u.fullName || u.name || "",
            name: u.name || u.fullName || "",
            university: u.university || "",
            profileImageUrl: u.profileImageUrl || "",
            isAdmin: !!u.isAdmin,
            unreadMessages: u.unreadMessages || 0,
            winsenseRating: u.winsenseRating || 1200,
            winsenseRank: u.winsenseRank || "",
          });
        }
      } catch (_) {
        // user state will be null, PrivateRoute will redirect back
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error(err);
      setError("Could not reach the server.");
      setSubmitting(false);
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!fullName || !email || !password) {
      setError("Please fill in all fields.");
      return;
    }

    if (fullName.trim().length < 2 || fullName.trim().length > 40) {
      setError("Name must be between 2 and 40 characters.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        fullName,
        email,
        password,
        csrfToken: csrfToken || "",
      }).toString();

      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setError(getSafeError(data, "Registration failed. Please try again."));
        setSubmitting(false);
        return;
      }

      // success -> verify
      setMode("verify");
      setPassword(""); // optional: clear password after register
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      setError("Could not reach the server.");
      setSubmitting(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!email) {
      setError("Please enter your e-mail address.");
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email, csrfToken: csrfToken || "" }).toString();

      const res = await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setError(getSafeError(data, "Request failed. Please try again."));
        setSubmitting(false);
        return;
      }

      setNotice(data.message || "If this e-mail exists in our system, we sent a reset link.");
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      setError("Could not reach the server.");
      setSubmitting(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!email || !verifyCode) {
      setError("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email, code: verifyCode, csrfToken: csrfToken || "" }).toString();

      const res = await fetch("/api/verify", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setError(getSafeError(data, "Verification failed. Please try again."));
        setSubmitting(false);
        return;
      }

      setVerifyCode("");
      setMode("signin");
      setNotice("Verified. Please sign in.");
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      setError("Could not reach the server.");
      setSubmitting(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!resetToken) {
      setError("Invalid or expired link.");
      return;
    }
    if (!resetPass || !resetConfirm) {
      setError("Please fill in all fields.");
      return;
    }
    if (resetPass.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (resetPass !== resetConfirm) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        token: resetToken,
        password: resetPass,
        confirm: resetConfirm,
      }).toString();

      const res = await fetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body,
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setError(getSafeError(data, "Password reset failed. Please try again."));
        setSubmitting(false);
        return;
      }

      setResetPass("");
      setResetConfirm("");
      setMode("signin");
      setNotice("Password changed. Please sign in.");
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      setError("Could not reach the server.");
      setSubmitting(false);
    }
  }

  const caretDone = typed.length === brandText.length;

  const subtitle =
    mode === "signin"
      ? "Sign in to continue"
      : mode === "signup"
      ? "Create your account"
      : mode === "verify"
      ? "Enter your verification code"
      : mode === "forgot"
      ? "Reset your password"
      : "Set a new password";

  return (
    <div className="lp-page">
      <div className="lp-vignette" />

      <div className="lp-main-title" aria-hidden="true">
        grada.live
      </div>

      <div className="lp-main-content-grid" aria-hidden="true">
        <div className="lp-feature-item">
          <div className="lp-feature-title">🔒 UNIVERSITY ONLY</div>
          <p>
            Access restricted to verified university emails.
            <br />
            If you’re not in, you’re not in.
          </p>
          <div className="lp-connector-line lp-line-left" />
        </div>

        <div className="lp-feature-item">
          <div className="lp-feature-title">🔥 HAPPENING NOW</div>
          <p>Your campus, in real time.</p>
          <div className="lp-connector-line lp-line-center" />
        </div>

        <div className="lp-feature-item">
          <div className="lp-feature-title">🎭 NO FILTERS</div>
          <p>
            Real campus conversations.
            <br />
            No PR. No fake polish.
          </p>
          <div className="lp-connector-line lp-line-right" />
        </div>
      </div>

      <div className="lp-container">
        <button
          className={`lp-btn-entry ${open ? "lp-active" : ""}`}
          onClick={() => setOpen((v) => !v)}
          type="button"
          aria-expanded={open}
          aria-controls="lp-login-card"
        >
          {open ? (
            "Close"
          ) : (
            <svg
              className="lp-icon-door"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
          )}
        </button>

        <div id="lp-login-card" className={`lp-card ${open ? "" : "lp-hidden"}`}>
          <div className="lp-header">
            <div className={`lp-brand ${caretDone ? "lp-typing-done" : ""}`}>{typed}</div>
            <div className="lp-subtitle">{subtitle}</div>
          </div>

          {error && <div className="lp-error">{error}</div>}
          {notice && <div className="lp-notice">{notice}</div>}

          {mode === "signin" && (
            <form className="lp-form" onSubmit={handleSignin}>
              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="email">EMAIL</label>
                </div>
                <input
                  id="email"
                  type="email"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="password">PASSWORD</label>
                  <button
                    type="button"
                    className="lp-forgot-link lp-linkbtn"
                    onClick={() => setMode("forgot")}
                  >
                    Forgot?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <button className="lp-btn-primary" type="submit" disabled={submitting || !csrfToken}>
                {submitting ? "Signing in..." : "Sign in"}
                <span className="lp-arrow">→</span>
              </button>

              <div className="lp-footer">
                No account?{" "}
                <button type="button" className="lp-linkbtn" onClick={() => setMode("signup")}>
                  Sign up
                </button>
              </div>
            </form>
          )}

          {mode === "signup" && (
            <form className="lp-form" onSubmit={handleSignup}>
              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="fullName">FULL NAME</label>
                </div>
                <input
                  id="fullName"
                  type="text"
                  placeholder="Your name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  minLength={2}
                  maxLength={40}
                  required
                />
              </div>

              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="email2">EMAIL</label>
                </div>
                <input
                  id="email2"
                  type="email"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="password2">PASSWORD</label>
                </div>
                <input
                  id="password2"
                  type="password"
                  placeholder="Choose a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>

              <button className="lp-btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create account"}
                <span className="lp-arrow">→</span>
              </button>

              <div className="lp-footer">
                Already have an account?{" "}
                <button type="button" className="lp-linkbtn" onClick={() => setMode("signin")}>
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {mode === "verify" && (
            <form className="lp-form" onSubmit={handleVerify}>
              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="emailV">EMAIL</label>
                </div>
                <input
                  id="emailV"
                  type="email"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="codeV">CODE</label>
                </div>
                <input
                  id="codeV"
                  type="text"
                  placeholder="6-digit code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  inputMode="numeric"
                  required
                />
              </div>

              <button className="lp-btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Verifying..." : "Verify"}
                <span className="lp-arrow">→</span>
              </button>

              <div className="lp-footer">
                Want to sign in?{" "}
                <button type="button" className="lp-linkbtn" onClick={() => setMode("signin")}>
                  Back
                </button>
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <form className="lp-form" onSubmit={handleForgot}>
              <div className="lp-form-group">
                <div className="lp-label-row">
                  <label htmlFor="emailF">EMAIL</label>
                </div>
                <input
                  id="emailF"
                  type="email"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <button className="lp-btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send reset link"}
                <span className="lp-arrow">→</span>
              </button>

              <div className="lp-footer">
                Back to{" "}
                <button type="button" className="lp-linkbtn" onClick={() => setMode("signin")}>
                  sign in
                </button>
              </div>
            </form>
          )}

          {mode === "reset" && (
            <form className="lp-form" onSubmit={handleReset}>
              <div className="lp-form-group">
                <label htmlFor="rp1">NEW PASSWORD</label>
                <input
                  id="rp1"
                  type="password"
                  placeholder="New password"
                  value={resetPass}
                  onChange={(e) => setResetPass(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={!resetChecked || !!error}
                />
              </div>

              <div className="lp-form-group">
                <label htmlFor="rp2">CONFIRM</label>
                <input
                  id="rp2"
                  type="password"
                  placeholder="Confirm password"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={!resetChecked || !!error}
                />
              </div>

              <button
                className="lp-btn-primary"
                type="submit"
                disabled={submitting || !resetChecked || !!error}
              >
                {submitting ? "Saving..." : "Save new password"}
                <span className="lp-arrow">→</span>
              </button>

              <div className="lp-footer">
                Back to{" "}
                <button type="button" className="lp-linkbtn" onClick={() => setMode("signin")}>
                  sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
